import { TextField } from '@mui/material';
import { InputBaseComponentProps } from '@mui/material/InputBase';
import { Field, FieldProps, useFormikContext } from 'formik';
import { camelCase, get } from 'lodash';
import { forwardRef, useCallback, useEffect, useMemo } from 'react';
import { NumberFormatValues, PatternFormat } from 'react-number-format';

import { InputComponent } from '..';
import { normalizeSize } from '../helpers';

const PHONE_FORMAT = '(###) ### - ####';

interface PhoneInputProps extends InputBaseComponentProps {
  format?: string;
  mask?: string;
  onValueChange?: (values: NumberFormatValues) => void;
}

const PhoneInputComponent = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ format = PHONE_FORMAT, mask = '_', onValueChange, ...props }, ref) => (
    <PatternFormat
      {...props}
      getInputRef={ref}
      format={format}
      mask={mask}
      onValueChange={onValueChange}
    />
  ),
);

PhoneInputComponent.displayName = 'PhoneInputComponent';

/**
 * The phone field's validation. A partial number is an error whether or not
 * the field is required: optional means "may be left blank", not "may be left
 * half-typed" — otherwise a few stray digits save as the contact number.
 * Counts digits, so an already-formatted stored value ("555-123-4567") passes.
 * @param value - The field value (digits, or a previously stored string)
 * @param opts - Field options
 * @param opts.label - Field label, for the message
 * @param opts.required - Whether a blank value is an error
 * @returns The error message, or undefined when valid
 */
export function validatePhone(
  value: string | number | undefined | null,
  { label, required }: { label?: string; required?: boolean },
): string | undefined {
  const digits = (value ?? '').toString().replace(/\D/g, '');
  if (!digits) {
    return required ? `${label} is required.` : undefined;
  }
  if (digits.length < 10) {
    return `${label} must be a valid 10 digit U.S. phone number.`;
  }
  return undefined;
}

export interface InputPhoneProps extends Omit<InputComponent, 'size'> {
  format?: string;
  mask?: string;
  size?: 'small' | 'medium' | 'large';
  onChange?: (name: string, value: string) => void;
}

export function InputPhone({
  label,
  disabled,
  hidden,
  required,
  className,
  hint,
  nameOverride,
  placeholder,
  showLabel = !hidden,
  readOnly = false,
  format = PHONE_FORMAT,
  mask = '_',
  size: propSize = 'medium',
  onChange = undefined,
}: InputPhoneProps) {
  const {
    errors,
    setFieldTouched,
    setFieldValue,
    getFieldMeta,
    submitCount,
    values,
  } = useFormikContext<Record<string, string>>();

  const size = normalizeSize(propSize);

  const validate = useCallback(
    (value: string | number | undefined) =>
      validatePhone(value, { label, required }),
    [label, required],
  );

  const name = useMemo(
    () => nameOverride ?? camelCase(label),
    [label, nameOverride],
  );

  const error = useMemo(() => get(errors, name), [errors, name]);

  const inputPlaceholder = useMemo(
    () => placeholder ?? label?.replace(/[?:]/g, ''),
    [placeholder, label],
  );

  const handleChange = useCallback(
    (numberValues: NumberFormatValues) => {
      setFieldValue(name, numberValues.value, true);
      onChange?.(name, numberValues.value || '');
    },
    [name, setFieldValue, onChange],
  );

  // Handle setting field as touched once form submits so errors show thanks Formik
  useEffect(() => {
    const meta = getFieldMeta(name);
    if (submitCount > 0 && !meta.touched) {
      setFieldTouched(name, true);
    }
  }, [submitCount, getFieldMeta, setFieldTouched, name]);

  const value = get(values, name, '');

  return (
    <Field name={name} validate={validate}>
      {({ field: _field }: FieldProps) => (
        <TextField
          name={name}
          fullWidth
          variant="outlined"
          label={showLabel && label}
          className={className}
          disabled={disabled}
          hidden={hidden}
          required={required && !readOnly}
          placeholder={inputPlaceholder}
          slotProps={{
            inputLabel: { shrink: !!(showLabel && label) },
            input: {
              inputComponent: PhoneInputComponent,
              inputProps: {
                format,
                mask,
                onValueChange: handleChange,
                readOnly,
              },
            },
          }}
          size={size === 'large' ? 'medium' : size}
          value={value}
          error={!!error}
          helperText={error ? error : hint}
        />
      )}
    </Field>
  );
}
